import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { usePackManagement } from "../../../hooks/usePackManagement";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import styles from "./admin.module.css";

const PROVIDER_OPTIONS = [
  { value: "smile.one", label: "Smile.one" },
  { value: "yokcash", label: "Yokcash" },
  { value: "hopestore", label: "Hopestore" },
];

export const GameManagement = () => {
  const { theme, toggleTheme } = useTheme();
  const API_URL = import.meta.env.VITE_API_URL;
  const token = useAuth().token;
  const [showGameForm, setShowGameForm] = useState(false);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editingGame, setEditingGame] = useState(null);

  // Pack loading state
  const [packLoading, setPackLoading] = useState(false);
  const [pricingInfo, setPricingInfo] = useState(null);

  // Simplified game form
  const [gameForm, setGameForm] = useState({
    name: "",
    description: "",
    image: "",
    region: "",
    category: "Mobile Games",
  });

  // Pack addition states
  const [selectedProvider, setSelectedProvider] = useState("");
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [availablePacks, setAvailablePacks] = useState([]);
  const [selectedGamePacks, setSelectedGamePacks] = useState([]);

  const {
    editingPack,
    packForm,
    setPackForm,
    handleEditPack,
    handleUpdatePack,
    resetPackForm,
  } = usePackManagement(editingGame?._id || null);

  // Fetch games on component mount
  useEffect(() => {
    fetchGames();
  }, []);

  // Fetch products when provider changes
  useEffect(() => {
    if (selectedProvider) {
      fetchProviderProducts(selectedProvider);
      setSelectedProduct("");
      setAvailablePacks([]);
    } else {
      setAvailableProducts([]);
      setSelectedProduct("");
      setAvailablePacks([]);
    }
  }, [selectedProvider]);

  // Fetch packs when product changes
  useEffect(() => {
    if (selectedProvider && selectedProduct) {
      fetchProviderPacks(selectedProvider, selectedProduct);
    } else {
      setAvailablePacks([]);
    }
  }, [selectedProvider, selectedProduct]);

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/games`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (data.success) {
        setGames(data.games || []);
      } else {
        setError("Failed to fetch games");
      }
    } catch (error) {
      console.error("Error fetching games:", error);
      setError("Error fetching games");
    } finally {
      setLoading(false);
    }
  };

  const fetchProviderProducts = async (provider) => {
    try {
      const response = await fetch(
        `${API_URL}/games/provider/${provider}/products`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = await response.json();
      if (data.success) {
        // Normalize products to include id and displayName
        const normalizedProducts = (data.products || []).map((product) => ({
          id: product.id || product.name,
          displayName: product.displayName || product.name,
          raw: product,
        }));
        setAvailableProducts(normalizedProducts);
        console.log("Normalized products:", normalizedProducts);
      } else {
        setAvailableProducts([]);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setAvailableProducts([]);
    }
  };

  // Updated to use backend-calculated pricing
  const fetchProviderPacks = async (provider, productId) => {
    try {
      setPackLoading(true);
      const response = await fetch(
        `${API_URL}/games/provider/${provider}/products/${productId}/packs?includeSummary=true`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = await response.json();
      if (data.success) {
        // Backend now returns enriched packs with calculated pricing
        const normalized = (data.packs || []).map((item) => ({
          id: item.packId || item.id,
          name: item.name,
          description: item.description || "",
          image: item.image || "",
          amount: item.amount || 0,

          // Use backend-calculated prices
          baseCost: item.baseCost,
          retailPrice: item.retailPrice,
          resellerPrice: item.resellerPrice,
          costPrice: item.costPrice,
          vip1Price: item.vip1Price,
          vip2Price: item.vip2Price,
          vip3Price: item.vip3Price,

          // Pricing metadata
          range: item.range,
          calculatedAt: item.calculatedAt,
          hasErrors: item.error,

          provider,
          productId,
          raw: item,
        }));

        setAvailablePacks(normalized);

        // Store pricing info for display
        if (data.pricingSummaries) {
          setPricingInfo(data.pricingSummaries);
        }

        console.log("Enriched packs with pricing:", normalized);
      } else {
        setAvailablePacks([]);
        setError(data.message || "Failed to fetch packs");
      }
    } catch (error) {
      console.error("Error fetching packs:", error);
      setAvailablePacks([]);
      setError("Error fetching packs with pricing");
    } finally {
      setPackLoading(false);
    }
  };

  const handleGameSubmit = async (e) => {
    e.preventDefault();
    clearMessages();

    // Validation
    if (
      !gameForm.name.trim() ||
      !gameForm.description.trim() ||
      !gameForm.image.trim() ||
      !gameForm.region.trim()
    ) {
      const msg = "Please fill all required fields";
      setError(msg);
      toast.error(msg);
      return;
    }

    if (selectedGamePacks.length === 0) {
      const msg = "Please add at least one pack to the game";
      setError(msg);
      toast.error(msg);
      return;
    }

    // Ensure every pack has provider and productId
    if (selectedGamePacks.some((p) => !p.provider || !p.productId)) {
      const msg = "Every pack must have a provider and product ID";
      setError(msg);
      toast.error(msg);
      return;
    }

    try {
      setLoading(true);
      const url = editingGame
        ? `${API_URL}/games/${editingGame._id}`
        : `${API_URL}/games`;

      const response = await fetch(url, {
        method: editingGame ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...gameForm,
          packs: selectedGamePacks,
        }),
      });

      const data = await response.json();
      if (data.success) {
        const msg = editingGame
          ? "Game updated successfully!"
          : "Game created successfully!";
        setSuccess(msg);
        toast.success(msg);
        setShowGameForm(false);
        setEditingGame(null);
        resetGameForm();
        fetchGames();
      } else {
        const msg = data.message || "Failed to save game";
        setError(msg);
        toast.error(msg);
      }
    } catch (error) {
      console.error("Error saving game:", error);
      const msg = "Error saving game. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Updated to use backend-calculated prices
  const handleAddPackFromApi = (e) => {
    const packId = e.target.value;
    if (!packId) return;

    const pack = availablePacks.find((p) => p.id === packId);
    if (!pack) return;

    if (selectedGamePacks.some((p) => p.packId === pack.id)) {
      setError("Pack already added");
      toast.error("Pack already added");
      return;
    }

    // Use backend-calculated pricing instead of frontend calculations
    const newPack = {
      packId: pack.id,
      name: pack.name,
      description: pack.description,
      image: pack.image,
      amount: pack.amount,

      // Use backend-calculated prices
      retailPrice: pack.retailPrice,
      resellerPrice: pack.resellerPrice,
      costPrice: pack.costPrice,
      vip1Price: pack.vip1Price,
      vip2Price: pack.vip2Price,
      vip3Price: pack.vip3Price,

      // Additional pricing metadata
      baseCost: pack.baseCost,
      range: pack.range,
      calculatedAt: pack.calculatedAt,

      provider: pack.provider,
      productId: pack.productId,
    };

    setSelectedGamePacks([...selectedGamePacks, newPack]);
    e.target.value = "";
    clearMessages();
  };

  // Manual add/edit pack (keep validation for manual entry)
  const handleAddPackManually = async () => {
    if (
      !packForm.packId ||
      !packForm.name ||
      !packForm.amount ||
      !packForm.retailPrice ||
      !packForm.resellerPrice ||
      !packForm.costPrice ||
      !packForm.provider ||
      !packForm.productId
    ) {
      const msg = "Please fill all required pack fields (including provider and product ID)";
      setError(msg);
      toast.error(msg);
      return;
    }

    if (editingPack) {
      // For existing games, use the hook's update function
      if (editingGame?._id) {
        const ok = await handleUpdatePack();
        if (ok) {
          const updated = [...selectedGamePacks];
          const idx = selectedGamePacks.findIndex(
            (p) => p.packId === editingPack.packId,
          );
          if (idx > -1) {
            updated[idx] = { ...packForm };
            setSelectedGamePacks(updated);
          }
        }
      } else {
        // For new games, update the local state directly
        const updated = [...selectedGamePacks];
        const idx = selectedGamePacks.findIndex(
          (p) => p.packId === editingPack.packId,
        );
        if (idx > -1) {
          updated[idx] = { ...packForm };
          setSelectedGamePacks(updated);
        }
        resetPackForm();
      }
    } else {
      // Adding new pack
      const exists = selectedGamePacks.some(
        (p) => p.packId === packForm.packId,
      );
      if (exists) {
        setError("Pack ID already exists");
        toast.error("Pack ID already exists");
        return;
      }
      setSelectedGamePacks((prev) => [...prev, { ...packForm }]);
      resetPackForm();
    }
    clearMessages();
  };

  const handleEditGame = (game) => {
    setEditingGame(game);
    setGameForm({
      name: game.name,
      description: game.description,
      image: game.image,
      region: game.region,
      category: game.category,
    });
    setSelectedGamePacks(game.packs || []);
    setShowGameForm(true);
    clearMessages();
  };

  const handleDeleteGame = async (gameId) => {
    if (!window.confirm("Are you sure you want to delete this game?")) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/games/${gameId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (data.success) {
        toast.success("Game deleted successfully!");
        setSuccess("Game deleted successfully!");
        fetchGames();
      } else {
        const msg = data.message || "Failed to delete game";
        setError(msg);
        toast.error(msg);
      }
    } catch (error) {
      console.error("Error deleting game:", error);
      const msg = "Error deleting game";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const resetGameForm = () => {
    setGameForm({
      name: "",
      description: "",
      image: "",
      region: "",
      category: "Mobile Games",
    });
    setSelectedGamePacks([]);
    setSelectedProvider("");
    setAvailableProducts([]);
    setSelectedProduct("");
    setAvailablePacks([]);
    setPricingInfo(null);
    resetPackForm();
  };

  const handleRemovePack = (index) => {
    const updated = selectedGamePacks.filter((_, i) => i !== index);
    setSelectedGamePacks(updated);
    clearMessages();
  };

  // Helper function to display pricing information
  const renderPricingInfo = (pack) => {
    if (!pack.range) return null;

    return (
      <div className={styles.pricingInfo}>
        <small className="text-muted">
          Range:{" "}
          <span className={`badge ${styles.badgeInfo}`}>{pack.range}</span>
          {pack.calculatedAt && (
            <span className="ms-2">
              Calculated: {new Date(pack.calculatedAt).toLocaleTimeString()}
            </span>
          )}
          {pack.hasErrors && (
            <span className={`badge ${styles.badgeWarning} ms-2`}>
              Fallback Pricing
            </span>
          )}
        </small>
      </div>
    );
  };

  return (
    <div className={styles.gameManagement}>
      {/* Header with Theme Toggle */}
      <div className={styles.header}>
        <h1 className={styles.title}>Game Management</h1>
        <button onClick={toggleTheme} className={styles.themeToggle}>
          {theme === "light" ? "🌙 Dark" : "☀️ Light"}
        </button>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className={`${styles.alert} ${styles.alertDanger}`}>{error}</div>
      )}
      {success && (
        <div className={`${styles.alert} ${styles.alertSuccess}`}>
          {success}
        </div>
      )}

      {/* Games List */}
      <div className="row">
        <div className="col-12">
          <div
            className={`d-flex justify-content-between align-items-center mb-4 ${styles.sectionDivider}`}
          >
            <h2 className="h3 mb-0">Games List</h2>
            <button
              onClick={() => {
                setShowGameForm(true);
                resetGameForm();
                setEditingGame(null);
                clearMessages();
                resetPackForm();
              }}
              disabled={loading}
              className={styles.addButton}
            >
              Add New Game
            </button>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h5 className={styles.cardTitle}>Games Overview</h5>
            </div>
            <div className={styles.cardBody}>
              {loading && !showGameForm ? (
                <div className={styles.loading}>
                  <div className={styles.spinner}></div>
                  <p>Loading games...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className={`table ${styles.table}`}>
                    <thead>
                      <tr>
                        <th>Game</th>
                        <th>Region</th>
                        <th>Packs</th>
                        <th>Providers</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {games.length === 0 ? (
                        <tr>
                          <td
                            colSpan="5"
                            className={`text-center py-4 ${styles.emptyState}`}
                          >
                            <div className={styles.emptyStateIcon}>🎮</div>
                            <p>No games found. Create your first game!</p>
                          </td>
                        </tr>
                      ) : (
                        games.map((game) => {
                          const providers = [
                            ...new Set(
                              game.packs?.map((p) => p.provider) || [],
                            ),
                          ];
                          return (
                            <tr key={game._id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <img
                                    className={styles.gameImage}
                                    src={game.image}
                                    alt={game.name}
                                    onError={(e) => {
                                      e.target.src =
                                        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjZGRkIi8+Cjx0ZXh0IHg9IjIwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzk5OSI+Tm8gSW1hZ2U8L3RleHQ+Cjwvc3ZnPg==";
                                    }}
                                  />
                                  <div>
                                    <div className="fw-medium">{game.name}</div>
                                    <small className="text-muted">
                                      {game.category}
                                    </small>
                                  </div>
                                </div>
                              </td>
                              <td>{game.region}</td>
                              <td>{game.packs?.length || 0} packs</td>
                              <td>
                                {providers.map((provider, idx) => (
                                  <span
                                    key={`${game._id}-${provider}-${idx}`}
                                    className={styles.badgePrimary}
                                  >
                                    {provider}
                                  </span>
                                ))}
                              </td>
                              <td>
                                <button
                                  onClick={() => handleEditGame(game)}
                                  className={styles.buttonPrimary}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteGame(game._id)}
                                  className={styles.buttonDanger}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Game Form Modal */}
      {showGameForm && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h5 className={styles.modalTitle}>
                {editingGame ? "Edit Game" : "Add New Game"}
              </h5>
              <button
                type="button"
                className={styles.closeButton}
                onClick={() => {
                  setShowGameForm(false);
                  setEditingGame(null);
                  resetGameForm();
                  clearMessages();
                }}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <form onSubmit={handleGameSubmit} id="gameForm">
                {/* Basic Game Info */}
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className={styles.formLabel}>Game Name *</label>
                    <input
                      type="text"
                      className={styles.formControl}
                      value={gameForm.name}
                      onChange={(e) =>
                        setGameForm({ ...gameForm, name: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className={styles.formLabel}>Region *</label>
                    <input
                      type="text"
                      className={styles.formControl}
                      value={gameForm.region}
                      onChange={(e) =>
                        setGameForm({ ...gameForm, region: e.target.value })
                      }
                      placeholder="e.g., Global, Asia, Brazil"
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className={styles.formLabel}>Category</label>
                    <select
                      className={`${styles.formControl} ${styles.formSelect}`}
                      value={gameForm.category}
                      onChange={(e) =>
                        setGameForm({ ...gameForm, category: e.target.value })
                      }
                    >
                      <option value="Mobile Games">Mobile Games</option>
                      <option value="PC Games">PC Games</option>
                      <option value="Console Games">Console Games</option>
                      <option value="Voucher">Voucher</option>
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className={styles.formLabel}>Image URL *</label>
                    <input
                      type="url"
                      className={styles.formControl}
                      value={gameForm.image}
                      onChange={(e) =>
                        setGameForm({ ...gameForm, image: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="col-12">
                    <label className={styles.formLabel}>Description *</label>
                    <textarea
                      className={`${styles.formControl} ${styles.textarea}`}
                      rows="3"
                      value={gameForm.description}
                      onChange={(e) =>
                        setGameForm({
                          ...gameForm,
                          description: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>

                {/* Pack Management Section */}
                <div className={styles.sectionDivider}>
                  <h5 className={styles.sectionTitle}>Game Packs</h5>

                  {/* Provider and Product Selection */}
                  <div className={styles.packCard}>
                    <h6>Add Pack from API</h6>
                    <div className="row g-2 mb-3">
                      <div className="col-md-4">
                        <label className={styles.formLabel}>Provider</label>
                        <select
                          className={`${styles.formControl} ${styles.formSelect}`}
                          value={selectedProvider}
                          onChange={(e) => setSelectedProvider(e.target.value)}
                        >
                          <option value="">Select Provider</option>
                          {PROVIDER_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className={styles.formLabel}>Product</label>
                        <select
                          className={`${styles.formControl} ${styles.formSelect}`}
                          value={selectedProduct}
                          onChange={(e) => setSelectedProduct(e.target.value)}
                          disabled={
                            !selectedProvider || availableProducts.length === 0
                          }
                        >
                          <option value="">Select Product</option>
                          {availableProducts.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.displayName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className={styles.formLabel}>Pack</label>
                        <select
                          className={`${styles.formControl} ${styles.formSelect}`}
                          onChange={handleAddPackFromApi}
                          disabled={
                            !selectedProduct ||
                            availablePacks.length === 0 ||
                            packLoading
                          }
                          defaultValue=""
                        >
                          <option value="" disabled>
                            {packLoading
                              ? "Loading packs..."
                              : "Choose a pack to add"}
                          </option>
                          {availablePacks.map((p, idx) => (
                            <option key={`pack-${p.id}-${idx}`} value={p.id}>
                              {p.name} — ₹{p.retailPrice} ({p.range})
                              {p.hasErrors && " ⚠️"}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Pack Loading Indicator */}
                    {packLoading && (
                      <div className={styles.packLoading}>
                        <div className={styles.spinner}></div>
                        <span>Calculating pack prices...</span>
                      </div>
                    )}
                  </div>

                  {/* Manual Pack Entry */}
                  <div className={styles.packCard}>
                    <h6>
                      {editingPack !== null ? "Edit Pack" : "Add Pack Manually"}
                    </h6>
                    <div className="row g-2">
                      <div className="col-md-2">
                        <label className={styles.formLabel}>Pack ID</label>
                        <input
                          type="text"
                          className={styles.formControl}
                          placeholder="Pack ID"
                          value={packForm.packId}
                          onChange={(e) =>
                            setPackForm({ ...packForm, packId: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-2">
                        <label className={styles.formLabel}>Pack Name</label>
                        <input
                          type="text"
                          className={styles.formControl}
                          placeholder="Pack Name"
                          value={packForm.name}
                          onChange={(e) =>
                            setPackForm({ ...packForm, name: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-2">
                        <label className={styles.formLabel}>Amount</label>
                        <input
                          type="number"
                          className={styles.formControl}
                          placeholder="Amount"
                          value={packForm.amount}
                          onChange={(e) =>
                            setPackForm({ ...packForm, amount: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-2">
                        <label className={styles.formLabel}>Retail Price</label>
                        <input
                          type="number"
                          step="0.01"
                          className={styles.formControl}
                          placeholder="Retail Price"
                          value={packForm.retailPrice}
                          onChange={(e) =>
                            setPackForm({
                              ...packForm,
                              retailPrice: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="col-md-2">
                        <label className={styles.formLabel}>Provider</label>
                        <select
                          className={`${styles.formControl} ${styles.formSelect}`}
                          value={packForm.provider}
                          onChange={(e) =>
                            setPackForm({
                              ...packForm,
                              provider: e.target.value,
                            })
                          }

                        >
                          <option value="">Select Provider</option>
                          {PROVIDER_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label className={styles.formLabel}>Product ID</label>
                        <input
                          type="text"
                          className={styles.formControl}
                          placeholder="Product ID"
                          value={packForm.productId}
                          onChange={(e) =>
                            setPackForm({
                              ...packForm,
                              productId: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="row g-2 mt-2">
                      <div className="col-md-3">
                        <label className={styles.formLabel}>
                          Reseller Price
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className={styles.formControl}
                          placeholder="Reseller Price"
                          value={packForm.resellerPrice}
                          onChange={(e) =>
                            setPackForm({
                              ...packForm,
                              resellerPrice: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="col-md-3">
                        <label className={styles.formLabel}>Cost Price</label>
                        <input
                          type="number"
                          step="0.01"
                          className={styles.formControl}
                          placeholder="Cost Price"
                          value={packForm.costPrice}
                          onChange={(e) =>
                            setPackForm({
                              ...packForm,
                              costPrice: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className={styles.formLabel}>Image URL</label>
                        <input
                          type="url"
                          className={styles.formControl}
                          placeholder="Pack Image URL"
                          value={packForm.image}
                          onChange={(e) =>
                            setPackForm({ ...packForm, image: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={handleAddPackManually}
                        className={
                          editingPack !== null
                            ? styles.buttonPrimary
                            : styles.buttonSuccess
                        }
                      >
                        {editingPack !== null ? "Update Pack" : "Add Pack"}
                      </button>
                      {editingPack !== null && (
                        <button
                          type="button"
                          onClick={resetPackForm}
                          className={`${styles.buttonSecondary} ms-2`}
                        >
                          Cancel Edit
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Enhanced Packs List with Pricing Information */}
                  {selectedGamePacks.length > 0 && (
                    <div className="table-responsive">
                      <table className={`table table-sm ${styles.table}`}>
                        <thead>
                          <tr>
                            <th>Image</th>
                            <th>Pack ID</th>
                            <th>Name</th>
                            <th>Amount</th>
                            <th>Provider</th>
                            <th>Product</th>
                            <th>Cost</th>
                            <th>Retail</th>
                            <th>Reseller</th>
                            <th>VIP Prices</th>
                            <th>Range</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedGamePacks.map((pack, index) => (
                            <tr key={index}>
                              <td>
                                {pack.image ? (
                                  <img
                                    src={pack.image}
                                    alt={pack.name}
                                    style={{
                                      width: "30px",
                                      height: "30px",
                                      objectFit: "cover",
                                    }}
                                    onError={(e) => {
                                      e.target.src = "/api/placeholder/30/30";
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: "30px",
                                      height: "30px",
                                      backgroundColor: "#f0f0f0",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "10px",
                                    }}
                                  >
                                    No Image
                                  </div>
                                )}
                              </td>
                              <td>{pack.packId}</td>
                              <td>
                                {pack.name}
                                {renderPricingInfo(pack)}
                              </td>
                              <td>{pack.amount}</td>
                              <td>
                                <span className={styles.badgeInfo}>
                                  {pack.provider}
                                </span>
                              </td>
                              <td>
                                <small>{pack.productId}</small>
                              </td>
                              <td>₹{pack.costPrice}</td>
                              <td>₹{pack.retailPrice}</td>
                              <td>₹{pack.resellerPrice}</td>
                              <td>
                                <small>
                                  {pack.vip1Price && `VIP1: ₹${pack.vip1Price}`}
                                  <br />
                                  {pack.vip2Price && `VIP2: ₹${pack.vip2Price}`}
                                  <br />
                                  {pack.vip3Price && `VIP3: ₹${pack.vip3Price}`}
                                </small>
                              </td>
                              <td>
                                {pack.range && (
                                  <span className={`badge ${styles.badgeInfo}`}>
                                    {pack.range}
                                  </span>
                                )}
                                {pack.hasErrors && (
                                  <span
                                    className={`badge ${styles.badgeWarning} d-block mt-1`}
                                  >
                                    Fallback
                                  </span>
                                )}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => handleEditPack(pack)}
                                  className={`${styles.buttonPrimary} me-2`}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemovePack(index)}
                                  className={styles.buttonDanger}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </form>
            </div>
            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.buttonSecondary}
                onClick={() => {
                  setShowGameForm(false);
                  setEditingGame(null);
                  resetGameForm();
                  resetPackForm();
                  clearMessages();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="gameForm"
                disabled={loading}
                className={styles.buttonPrimary}
              >
                {loading
                  ? "Saving..."
                  : editingGame
                    ? "Update Game"
                    : "Create Game"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
